import React, { useRef, useEffect, useCallback } from 'react';
import type { GameState } from '@/types/game';
import { GameStatus } from '@/types/game';

import { renderGame, calculateCellSize } from '@/game/renderer';

// ============================================================================
// 类型定义
// ============================================================================

/**
 * GameCanvas 组件的 Props 接口。
 *
 * 组件接收完整的游戏状态和一个 tick 回调，
 * 负责将状态渲染到 Canvas 上，并通过 requestAnimationFrame 驱动游戏循环。
 */
export interface GameCanvasProps {
  /** 完整的游戏状态（包含所有实体、配置和粒子） */
  gameState: GameState;
  /** 每帧调用的 tick 回调，传入自上一帧以来的时间增量（毫秒） */
  onTick: (deltaTime: number) => void;
}

// ============================================================================
// 常量
// ============================================================================

/**
 * 窗口大小变化防抖延迟（毫秒）。
 *
 * 在移动端设备旋转或桌面端窗口拖拽时，
 * 避免频繁触发 Canvas 尺寸重算导致的性能抖动。
 */
const RESIZE_DEBOUNCE_MS = 150;

// ============================================================================
// GameCanvas 组件
// ============================================================================

/**
 * 霓虹蛇境 - 游戏画布组件
 *
 * 核心渲染组件，负责：
 * 1. 管理 HTML5 Canvas 元素的生命周期
 * 2. 根据容器尺寸动态计算单元格大小
 * 3. 通过 requestAnimationFrame 驱动游戏渲染循环
 * 4. 响应窗口大小变化，自动调整画布尺寸
 *
 * 组件设计原则：
 * - Canvas 元素由本组件完全拥有和管理
 * - 游戏状态通过 props 传入，组件不持有游戏状态
 * - 渲染逻辑委托给 renderer 模块的纯函数
 * - 游戏循环在 Playing 状态或有活跃粒子时持续运行
 *
 * @param props - 组件属性
 * @returns Canvas 元素（包裹在 Fragment 中）
 */
const GameCanvas: React.FC<GameCanvasProps> = ({ gameState, onTick }) => {
  // ==========================================================================
  // Refs
  // ==========================================================================

  /** Canvas DOM 元素的引用 */
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  /** 当前计算得到的单元格像素大小 */
  const cellSizeRef = useRef<number>(0);

  /** requestAnimationFrame 返回的动画帧 ID，用于取消循环 */
  const animationFrameIdRef = useRef<number>(0);

  /** 上一帧的时间戳（毫秒），用于计算 deltaTime */
  const lastTimeRef = useRef<number>(0);

  /** 窗口大小变化防抖计时器 ID */
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ==========================================================================
  // Canvas 尺寸计算与更新
  // ==========================================================================

  /**
   * 根据父容器尺寸重新计算并更新 Canvas 的像素尺寸。
   *
   * 计算流程：
   * 1. 获取 Canvas 的父容器元素
   * 2. 读取容器的 clientWidth 和 clientHeight
   * 3. 使用 calculateCellSize 计算最优单元格大小
   * 4. 更新 Canvas 的 width/height 属性（像素）
   * 5. 将 cellSize 存储到 ref 中供渲染循环使用
   *
   * 注意：直接修改 Canvas 的 width/height 属性会清除画布内容，
   * 但由于渲染循环在每帧都会重绘，这不会造成视觉问题。
   */
  const updateCanvasSize = useCallback((): void => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const container = canvas.parentElement;
    if (!container) {
      return;
    }

    // 获取容器可用空间（减去边框和内边距）
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // 防止无效尺寸
    if (containerWidth <= 0 || containerHeight <= 0) {
      return;
    }

    // 计算最优单元格大小
    const cellSize = calculateCellSize(
      gameState.grid,
      containerWidth,
      containerHeight
    );

    // 计算画布总像素尺寸
    const totalWidth = gameState.grid.width * cellSize;
    const totalHeight = gameState.grid.height * cellSize;

    // 仅在尺寸实际变化时更新（避免不必要的画布清除）
    if (
      canvas.width !== totalWidth ||
      canvas.height !== totalHeight ||
      cellSizeRef.current !== cellSize
    ) {
      canvas.width = totalWidth;
      canvas.height = totalHeight;
      cellSizeRef.current = cellSize;
    }
  }, [gameState.grid]);

  // ==========================================================================
  // 窗口大小变化监听
  // ==========================================================================

  /**
   * 监听窗口 resize 事件，使用防抖策略更新 Canvas 尺寸。
   *
   * 防抖延迟为 RESIZE_DEBOUNCE_MS（150ms），
   * 在快速连续调整窗口大小时避免过度重绘。
   *
   * 组件卸载时清除防抖计时器和事件监听器。
   */
  useEffect(() => {
    /**
     * 防抖处理后的 resize 事件处理器。
     * 清除之前的计时器，设置新的计时器延迟执行尺寸更新。
     */
    const handleResize = (): void => {
      if (resizeTimerRef.current !== null) {
        clearTimeout(resizeTimerRef.current);
      }
      resizeTimerRef.current = setTimeout(() => {
        updateCanvasSize();
        resizeTimerRef.current = null;
      }, RESIZE_DEBOUNCE_MS);
    };

    // 初始尺寸计算
    updateCanvasSize();

    // 注册 resize 监听器
    window.addEventListener('resize', handleResize);

    // 清理函数
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimerRef.current !== null) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
  }, [updateCanvasSize]);

  // ==========================================================================
  // 游戏渲染循环
  // ==========================================================================

  /**
   * 管理 requestAnimationFrame 游戏循环的生命周期。
   *
   * 循环运行条件：
   * - 游戏状态为 Playing（正常游戏循环）
   * - 游戏状态非 Playing 但存在活跃粒子（播放死亡动画等）
   *
   * 循环停止条件：
   * - 游戏状态非 Playing 且无活跃粒子
   *
   * 每帧执行：
   * 1. 计算 deltaTime（自上一帧以来的毫秒数）
   * 2. 调用 onTick(deltaTime) 更新游戏状态
   * 3. 获取 Canvas 2D 上下文
   * 4. 调用 renderGame 绘制当前帧
   * 5. 判断是否继续循环
   */
  useEffect(() => {
    // 获取 Canvas 2D 渲染上下文（在 effect 中获取以确保 Canvas 已挂载）
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('无法获取 Canvas 2D 渲染上下文');
      return;
    }

    // 判断是否需要运行动画循环
    const shouldAnimate =
      gameState.status === GameStatus.Playing ||
      gameState.particles.length > 0;

    if (!shouldAnimate) {
      // 不需要动画时，仅渲染一帧静态画面
      renderGame(ctx, gameState, cellSizeRef.current, performance.now());
      return;
    }

    // 重置时间基准，避免 deltaTime 异常
    lastTimeRef.current = 0;

    /**
     * 单帧渲染回调。
     *
     * 由 requestAnimationFrame 调用，传入高精度时间戳。
     * 首次调用时 deltaTime 为 0，后续帧计算实际时间差。
     *
     * @param timestamp - DOMHighResTimeStamp，当前帧的时间戳（毫秒）
     */
    const frame = (timestamp: number): void => {
      // 首帧初始化时间基准
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = timestamp;
      }

      // 计算时间增量（毫秒），限制最大值防止跳帧异常
      const deltaTime = Math.min(timestamp - lastTimeRef.current, 100);
      lastTimeRef.current = timestamp;

      // 通知父组件更新游戏状态
      onTick(deltaTime);

      // 渲染当前帧
      renderGame(ctx, gameState, cellSizeRef.current, timestamp);

      // 判断是否继续循环
      // 注意：此处使用闭包中的 gameState，可能不是最新值。
      // 但由于 React 会在状态更新后重新运行此 effect，
      // 旧循环会被取消，新循环会以最新状态启动。
      // 这里保留判断是为了在当前 effect 内部正确决策。
      const continueLoop =
        gameState.status === GameStatus.Playing ||
        gameState.particles.length > 0;

      if (continueLoop) {
        animationFrameIdRef.current = requestAnimationFrame(frame);
      }
    };

    // 启动动画循环
    animationFrameIdRef.current = requestAnimationFrame(frame);

    // 清理函数：取消未完成的动画帧
    return () => {
      if (animationFrameIdRef.current !== 0) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = 0;
      }
      lastTimeRef.current = 0;
    };
    // 注意：gameState 作为依赖项，状态变化时会重新创建循环。
    // 这是有意为之：确保渲染循环始终使用最新的游戏状态引用。
  }, [gameState, onTick]);

  // ==========================================================================
  // 渲染
  // ==========================================================================

  /**
   * 组件渲染输出。
   *
   * 仅返回 Canvas 元素，不添加额外的包裹 div。
   * 父组件（App.tsx 中的 .game-canvas-container）负责定位和样式。
   *
   * Canvas 的 width/height 属性由 updateCanvasSize 动态设置，
   * CSS 尺寸由全局样式表中的 .game-canvas 规则控制（width: 100%, height: auto）。
   */
  return (
    <canvas
      ref={canvasRef}
      className="game-canvas"
      aria-label="霓虹蛇境游戏画布"
      role="img"
    />
  );
};

// ============================================================================
// 导出
// ============================================================================

export default GameCanvas;